import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { usePerms } from 'contexts/PermsContext';
import { AuthContext } from 'features/Auth/contexts/AuthContext';
import usePlaces from 'features/UserMenu/features/Places/hooks/usePlaces';
import useLocation from 'hooks/useLocation';
import useTextToSpeech from 'hooks/useTextToSpeech';
import Cookies from 'js-cookie';
import { useContext, useState } from 'react';
import { Container } from 'react-bootstrap';
import { FaRegUserCircle } from 'react-icons/fa';
import { FaUserPen } from 'react-icons/fa6';
import { FiLogOut } from 'react-icons/fi';
import { IoAccessibility, IoInformationCircleOutline } from 'react-icons/io5';
import { PiMapPinLineFill } from 'react-icons/pi';
import { RxHamburgerMenu } from 'react-icons/rx';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { logout } from 'services/auth/auth.service';
import { selectUsername } from 'store/selectors/User/userSelectors';
import { resetStore } from 'store/store';
import { clearCookies } from 'utils/sessions/cookie.utils';
import { getLogo } from 'utils/images/image.utils';
import styles from './assets/header.module.css';

const MobileHeader = ({
  isMobileNavOpen,
  setShowFull,
  setNewTermsFullyAgreed,
  setTermsFullyViewed,
  setHasSignedIn,
  setHasFetchedPerms,
  setCookiesAgreed,
  setPrivacyAgreed,
}) => {
  const dispatch = useDispatch();
  const username = useSelector(selectUsername);
  const { locations } = useLocation();
  const userSelectedCity = locations.effectiveLocation?.city;
  const [userMenuOverlay, setUserMenuOverlay] = useState(false);
  const tts = useTextToSpeech();
  const perms = usePerms();
  const isMsalAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  usePlaces();
  const { logout: authLogout } = authContext;

  const handleLogout = () => {
    if (isMsalAuthenticated) {
      instance.logoutRedirect({
        onRedirectNavigate: (url) => {
          return false;
        },
      });
    }
    setShowFull(true);

    // Call API logout
    logout();

    // Keep remember me data if set
    let keepCookies = [];
    if (Cookies.get('rememberMe') === 'true') {
      keepCookies = ['rememberMe', 'email'];
    }
    clearCookies(keepCookies);

    // Explicitly clear privacy and cookie agreement for legal compliance
    Cookies.remove('cookiesAgreed');
    Cookies.remove('privacyAgreed');
    setCookiesAgreed(false);
    setPrivacyAgreed(false);

    // Reset app state
    perms.reset();
    tts.cancel();
    setNewTermsFullyAgreed(false);
    setTermsFullyViewed(false);
    setHasSignedIn(false);
    setHasFetchedPerms(false);

    // Use centralized auth logout
    authLogout();
    dispatch(resetStore());
    navigate('/');
  };

  return (
    <>
      <Container
        as="header"
        fluid
        id="mobile-header"
        className={`d-flex justify-content-around p-3 w-100 align-items-center bg-primary ${
          userMenuOverlay && 'position-fixed z-chrome'
        } `}
      >
        <RxHamburgerMenu
          size={30}
          onClick={() => {
            isMobileNavOpen(true);
            setUserMenuOverlay(false);
          }}
        />
        <Link
          to="/dashboard"
          className="cursor-pointer"
          aria-label="Go to dashboard"
          onClick={() => setUserMenuOverlay(false)}
        >
          <img src={getLogo(true)} alt="logo" height={59} />
        </Link>
        <FaRegUserCircle size={30} onClick={() => setUserMenuOverlay(!userMenuOverlay)} />
      </Container>
      <Container
        fluid
        className={`bg-primary-shadow z-chrome py-0 px-0 d-flex flex-column gap-4 position-fixed ${
          styles['user-menu']
        } ${userMenuOverlay && styles['user-menu-open']}`}
      >
        <div className="w-100 p-3 d-flex flex-column gap-3" style={{ background: '#232B33' }}>
          <div>
            <p className="fs-5 mb-0">Signed in as:</p>
            <p className="fs-5 mb-0">{username}</p>
          </div>
          <div>
            <p className="fs-5 mb-0">Currently in:</p>
            <p className="fs-5 mb-0">{userSelectedCity}</p>
          </div>
        </div>
        <div className="px-3">
          <Link
            to="/usermenu?tab=Account"
            className="text-white text-decoration-none d-flex gap-3 align-items-center fs-5"
            onClick={() => setUserMenuOverlay(false)}
          >
            <FaUserPen size={45} />
            Edit Account
          </Link>
        </div>
        <div className="px-3">
          <Link
            to="/usermenu?tab=Places"
            className="text-white text-decoration-none d-flex gap-3 align-items-center fs-5"
            onClick={() => setUserMenuOverlay(false)}
          >
            <PiMapPinLineFill size={45} />
            Places
          </Link>
        </div>
        <div className="px-3">
          <Link
            to="/usermenu?tab=Accessibility"
            className="text-white text-decoration-none d-flex gap-3 align-items-center fs-5"
            onClick={() => setUserMenuOverlay(false)}
          >
            <IoAccessibility size={45} />
            Accessibility Settings
          </Link>
        </div>
        <div className="px-3">
          <Link
            to="usermenu?tab=About"
            className="text-white text-decoration-none d-flex gap-3 align-items-center fs-5"
            onClick={() => setUserMenuOverlay(false)}
          >
            <IoInformationCircleOutline size={45} />
            About
          </Link>
        </div>
        <div className="px-3">
          <Link
            to="/"
            className="text-white text-decoration-none d-flex gap-3 align-items-center fs-5 mb-5"
            onClick={() => {
              setUserMenuOverlay(false);
              handleLogout();
            }}
          >
            <FiLogOut size={45} />
            Log Out
          </Link>
        </div>
      </Container>
    </>
  );
};

export default MobileHeader;
